import { useEffect, useState } from "react";
import { api } from "@/api";
import Shell from "@/components/Shell";
import { Spinner, Overline, Stat } from "@/components/Brand";
import { BarChart3, Trophy, LineChart as LineIcon, Sparkles, Store, Users2, CheckCircle2, BookOpen } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const adminNav = [
  { key: "overview", to: "/admin", label: "Overview", icon: BarChart3 },
];

const COLORS = ["#D4AF37", "#E0A96D", "#9A7B2C", "#F3E5AB", "#B8860B"];

export default function AdminDashboard() {
  const [ov, setOv] = useState(null);
  const [perf, setPerf] = useState(null);
  const [trends, setTrends] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingAi, setLoadingAi] = useState(true);

  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setOv(data));
    api.get("/admin/exhibitor-performance").then(({ data }) => setPerf(data));
    api.get("/admin/trends").then(({ data }) => setTrends(data));
    api.get("/admin/insights").then(({ data }) => setInsights(data)).finally(() => setLoadingAi(false));
  }, []);

  if (!ov || !perf || !trends) return <Shell nav={adminNav}><Spinner /></Shell>;

  return (
    <Shell nav={adminNav}>
      <Overline>JJS Oversight · Read-only</Overline>
      <h1 className="font-display text-3xl sm:text-4xl gold-text mt-2">Event Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat testid="admin-activation-stat-card" label="Activation Rate" value={`${ov.activation_rate}%`} icon={CheckCircle2} />
        <Stat testid="admin-exhibitors" label="Exhibitors" value={`${ov.activated_exhibitors}/${ov.total_exhibitors}`} icon={Store} />
        <Stat testid="admin-visitors" label="Visitors" value={ov.total_visitors} icon={Users2} />
        <Stat testid="admin-checkins" label="Check-ins" value={ov.total_checkins} icon={BarChart3} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat testid="admin-catalogues" label="Catalogues" value={ov.total_catalogues} icon={BookOpen} />
        <Stat testid="admin-saves" label="Catalogue Saves" value={ov.catalogue_saves} icon={BookOpen} />
      </div>

      <div className="glass rounded-3xl p-5 mt-6" data-testid="admin-ai-insights">
        <div className="flex items-center gap-2 text-amber-300 text-sm uppercase tracking-wide"><Sparkles size={15} /> AI Insights</div>
        {loadingAi ? <p className="text-slate-400 text-sm mt-3">Generating insights…</p> : (
          <>
            <p className="font-serif-head text-xl text-slate-100 mt-2">{insights?.headline}</p>
            <ul className="mt-3 space-y-2">
              {(insights?.insights || []).map((i, idx) => (
                <li key={idx} className="text-sm text-slate-300 flex gap-2"><span className="text-amber-400">•</span> {i}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <div className="glass rounded-3xl p-5">
          <div className="flex items-center gap-2 text-slate-200 mb-4"><LineIcon size={16} className="text-amber-400" /> Daywise Check-ins</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trends.daywise}>
              <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} />
              <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#12332A", border: "1px solid rgba(212,175,55,.3)", borderRadius: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="checkins" stroke="#D4AF37" strokeWidth={2.5} dot={{ fill: "#D4AF37" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="glass rounded-3xl p-5">
          <div className="flex items-center gap-2 text-slate-200 mb-4"><Users2 size={16} className="text-amber-400" /> Visitor Mix</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={trends.classifications} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                {trends.classifications.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#12332A", border: "1px solid rgba(212,175,55,.3)", borderRadius: 12, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass rounded-3xl p-5 mt-6">
        <div className="flex items-center gap-2 text-slate-200 mb-4"><Trophy size={16} className="text-amber-400" /> Exhibitor Performance</div>
        <ResponsiveContainer width="100%" height={Math.max(220, perf.length * 38)}>
          <BarChart data={perf} layout="vertical" margin={{ left: 20 }}>
            <XAxis type="number" stroke="#94A3B8" fontSize={11} allowDecimals={false} />
            <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={120} />
            <Tooltip contentStyle={{ background: "#12332A", border: "1px solid rgba(212,175,55,.3)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(212,175,55,.08)" }} />
            <Bar dataKey="visitors" fill="#D4AF37" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[11px] text-slate-500 mt-2">Aggregate engagement only. Private leads & visitor contacts are not shown.</p>
      </div>
    </Shell>
  );
}
